import * as React from 'react';
import { capitalize, unstable_useId as useId } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { MasonTheme } from '@bymason-private/controller-v1-theme-augmentation';
import {
  GridColDef,
  GridFilterItem,
  GridFilterOperator,
  GridLinkOperator,
  GridTranslationKeys,
  GridPanelWrapper,
  GridPanelContent,
  GridPanelFooter,
  GridPanelHeader,
  useGridApiContext,
  useGridSelector,
  useGridRootProps,
  gridFilterableColumnDefinitionsSelector,
  gridFilterModelSelector,
} from '@mui/x-data-grid-pro';
import { Button, ControllerSelect, IconButton, MdRemoveCircle, MdAddCircle, Text } from '../..';

const useStylesForm = makeStyles((theme: MasonTheme) => ({
  form: {
    display: 'flex',
    justifyContent: 'space-around',
    gap: 8,
    marginBottom: 8,
    marginRight: 16,
  },
  linkOperatorSelect: {
    width: 60,
    '& > *': {
      width: '100%',
    },
  },
  columnSelect: {
    width: 150,
  },
  operatorSelect: {
    width: 150,
  },
  filterValueInput: {
    width: 190,
    '& > *': {
      width: '100%',
    },
  },
  closeIcon: {
    color: theme.palette.error.main,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    marginBottom: 4,
    padding: 2,
  },
}));

const useStylesDialog = makeStyles((theme: MasonTheme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    padding: '0 8px',
    ...theme.typography.h5,
  },
}));

export function ControllerGridFilterForm(props: ControllerGridFilterFormProps) {
  const {
    item,
    hasMultipleFilters,
    deleteFilter,
    applyFilterChanges,
    multiFilterOperator,
    showMultiFilterOperators,
    disableMultiFilterOperator,
    applyMultiFilterOperatorChanges,
  } = props;
  const classes = useStylesForm();
  const apiRef = useGridApiContext();
  const filterableColumns = useGridSelector(apiRef, gridFilterableColumnDefinitionsSelector);
  const linkOperatorSelectId = useId();
  const linkOperatorSelectLabelId = useId();
  const columnSelectId = useId();
  const columnSelectLabelId = useId();
  const operatorSelectId = useId();
  const operatorSelectLabelId = useId();
  const [currentColumn, setCurrentColumn] = React.useState<GridColDef | null>(() => {
    if (!item.columnField) {
      return null;
    }
    return apiRef.current.getColumn(item.columnField);
  });
  const [currentOperator, setCurrentOperator] = React.useState<GridFilterOperator | null>(() => {
    if (!item.operatorValue || !currentColumn) {
      return null;
    }

    return currentColumn.filterOperators?.find((operator) => operator.value === item.operatorValue) || null;
  });

  const changeColumn = React.useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      const columnField = event.target.value as string;
      const column = apiRef.current.getColumn(columnField);
      const newOperator = column?.filterOperators?.[0] || null;
      setCurrentOperator(newOperator);
      setCurrentColumn(column);

      applyFilterChanges({
        ...item,
        value: undefined,
        columnField,
        operatorValue: newOperator?.value,
      });
    },
    [apiRef, applyFilterChanges, item]
  );

  const changeOperator = React.useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      const operatorValue = event.target.value as string;
      applyFilterChanges({
        ...item,
        operatorValue,
      });
      const newOperator =
        currentColumn?.filterOperators?.find((operator) => operator.value === operatorValue) || null;
      setCurrentOperator(newOperator);
    },
    [applyFilterChanges, currentColumn, item]
  );

  const changeLinkOperator = React.useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      const linkOperator =
        (event.target.value as string) === GridLinkOperator.And.toString()
          ? GridLinkOperator.And
          : GridLinkOperator.Or;
      applyMultiFilterOperatorChanges(linkOperator);
    },
    [applyMultiFilterOperatorChanges]
  );

  const handleDeleteFilter = React.useCallback(() => {
    deleteFilter(item);
  }, [deleteFilter, item]);

  return (
    <div className={classes.form}>
      <div className={classes.closeIcon}>
        <IconButton
          color='inherit'
          aria-label={apiRef.current.getLocaleText('filterPanelDeleteIconLabel')}
          title={apiRef.current.getLocaleText('filterPanelDeleteIconLabel')}
          onClick={handleDeleteFilter}
          size='small'
        >
          <MdRemoveCircle fontSize={20} />
        </IconButton>
      </div>
      <ControllerSelect
        className={classes.linkOperatorSelect}
        label={apiRef.current.getLocaleText('filterPanelOperators')}
        id={linkOperatorSelectId}
        value={multiFilterOperator}
        onChange={changeLinkOperator}
        disabled={!!disableMultiFilterOperator}
        InputLabelProps={{ htmlFor: linkOperatorSelectId, id: linkOperatorSelectLabelId }}
        SelectProps={{ native: true }}
        style={{
          display: hasMultipleFilters ? 'block' : 'none',
          visibility: showMultiFilterOperators ? 'visible' : 'hidden',
        }}
      >
        <option key={GridLinkOperator.And.toString()} value={GridLinkOperator.And.toString()}>
          {apiRef.current.getLocaleText('filterPanelOperatorAnd')}
        </option>
        <option key={GridLinkOperator.Or.toString()} value={GridLinkOperator.Or.toString()}>
          {apiRef.current.getLocaleText('filterPanelOperatorOr')}
        </option>
      </ControllerSelect>

      <ControllerSelect
        className={classes.columnSelect}
        label={apiRef.current.getLocaleText('filterPanelColumns')}
        id={columnSelectId}
        value={item.columnField || ''}
        onChange={changeColumn}
        InputLabelProps={{ htmlFor: columnSelectId, id: columnSelectLabelId }}
        SelectProps={{ native: true }}
      >
        {filterableColumns.map((col) => (
          <option key={col.field} value={col.field}>
            {col.headerName || col.field}
          </option>
        ))}
      </ControllerSelect>

      <ControllerSelect
        className={classes.operatorSelect}
        label={apiRef.current.getLocaleText('filterPanelOperators')}
        id={operatorSelectId}
        value={item.operatorValue}
        onChange={changeOperator}
        InputLabelProps={{ htmlFor: operatorSelectId, id: operatorSelectLabelId }}
        SelectProps={{ native: true }}
      >
        {currentColumn?.filterOperators?.map((operator) => (
          <option key={operator.value} value={operator.value}>
            {operator.label ||
              apiRef.current.getLocaleText(
                `filterOperator${capitalize(operator.value)}` as GridTranslationKeys
              )}
          </option>
        ))}
      </ControllerSelect>

      <div className={classes.filterValueInput}>
        {currentOperator?.InputComponent && (
          <currentOperator.InputComponent
            apiRef={apiRef}
            item={item}
            applyValue={applyFilterChanges}
            {...currentOperator.InputComponentProps}
          />
        )}
      </div>
    </div>
  );
}

export function ControllerGridFilterPanel() {
  const classes = useStylesDialog();
  const apiRef = useGridApiContext();
  const { disableMultipleColumnsFiltering } = useGridRootProps();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const filterableColumns = useGridSelector(apiRef, gridFilterableColumnDefinitionsSelector);

  const applyFilter = React.useCallback(
    (item: GridFilterItem) => {
      apiRef.current.upsertFilterItem(item);
    },
    [apiRef]
  );

  const applyFilterLinkOperator = React.useCallback(
    (operator: GridLinkOperator) => {
      apiRef.current.setFilterLinkOperator(operator);
    },
    [apiRef]
  );

  const addNewFilter = React.useCallback(() => {
    apiRef.current.upsertFilterItem({ columnField: '' });
  }, [apiRef]);

  const deleteFilter = React.useCallback(
    (item: GridFilterItem) => {
      apiRef.current.deleteFilterItem(item);
    },
    [apiRef]
  );

  const getDefaultItem = React.useCallback((): GridFilterItem | null => {
    const firstColumnWithOperator = filterableColumns.find((colDef) => colDef.filterOperators?.length);

    if (!firstColumnWithOperator) {
      return null;
    }

    return {
      columnField: firstColumnWithOperator.field,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      operatorValue: firstColumnWithOperator.filterOperators![0].value,
      id: Math.round(Math.random() * 1e5),
    };
  }, [filterableColumns]);

  const items = React.useMemo<GridFilterItem[]>(() => {
    if (filterModel.items.length) {
      return filterModel.items;
    }

    const defaultItem = getDefaultItem();

    return defaultItem ? [defaultItem] : [];
  }, [filterModel.items, getDefaultItem]);

  React.useEffect(() => {
    if (items.length === 0) {
      addNewFilter();
    }
  }, [addNewFilter, items.length]);

  const hasMultipleFilters = items.length > 1;

  return (
    <GridPanelWrapper>
      <GridPanelHeader className={classes.header}>
        <Text className={classes.title} component='span'>
          Filters
        </Text>
      </GridPanelHeader>
      <GridPanelContent>
        {items.map((item, index) => (
          <ControllerGridFilterForm
            key={item.id}
            item={item}
            applyFilterChanges={applyFilter}
            deleteFilter={deleteFilter}
            hasMultipleFilters={hasMultipleFilters}
            showMultiFilterOperators={index > 0}
            multiFilterOperator={filterModel.linkOperator}
            disableMultiFilterOperator={index !== 1}
            applyMultiFilterOperatorChanges={applyFilterLinkOperator}
          />
        ))}
      </GridPanelContent>
      {!disableMultipleColumnsFiltering && (
        <GridPanelFooter>
          <Button onClick={addNewFilter} startIcon={<MdAddCircle />} color='primary'>
            {apiRef.current.getLocaleText('filterPanelAddFilter')}
          </Button>
        </GridPanelFooter>
      )}
    </GridPanelWrapper>
  );
}

export interface ControllerGridFilterFormProps {
  item: GridFilterItem;
  hasMultipleFilters: boolean;
  showMultiFilterOperators?: boolean;
  multiFilterOperator?: GridLinkOperator;
  disableMultiFilterOperator?: boolean;
  applyFilterChanges: (item: GridFilterItem) => void;
  applyMultiFilterOperatorChanges: (operator: GridLinkOperator) => void;
  deleteFilter: (item: GridFilterItem) => void;
}
